import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import NewsLayout from "../../components/news/NewsLayout";
import { Skeleton } from "../../components/ui/skeleton";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Twitter, Facebook, Linkedin, Link as LinkIcon, Clock } from "lucide-react";
import { useProject } from "../../context/ProjectContext";
import { themeFromHost, siteNameFromHost } from "../../lib/themeFromHost";
import Seo, { newsArticleLd, breadcrumbLd, stripHtmlToText, clipText } from "../../components/news/Seo";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const formatReadTime = (content) => {
  const words = (content || "").split(/\s+/).length;
  return Math.max(1, Math.round(words / 220));
};

const NewsArticle = () => {
  const { slug } = useParams();
  const { publicProject, publicProjectLoading } = useProject();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (publicProjectLoading) return;
    const fetchArticle = async () => {
      setLoading(true);
      try {
        if (!publicProject) {
          setArticle(null);
          return;
        }
        const articleRes = await axios.get(`${API}/public/articles/${slug}?project_id=${publicProject.id}`);
        setArticle(articleRes.data);
        const relRes = await axios.get(`${API}/public/articles?project_id=${publicProject.id}&category=${articleRes.data.category}&limit=10`);
        setRelated((relRes.data || []).filter((a) => a.id !== articleRes.data.id).slice(0, 4));
      } catch (e) {
        console.error("Failed to fetch article:", e);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug, publicProject, publicProjectLoading]);

  const theme = publicProject?.theme || themeFromHost();
  const siteName = publicProject?.name || siteNameFromHost() || "News";
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const articleUrl = typeof window !== "undefined" ? window.location.href : "";

  if (loading) {
    return (
      <NewsLayout theme={theme} siteName={siteName}>
        <div className="max-w-3xl mx-auto px-6 py-16">
          <Skeleton className="h-8 w-32 mb-4" />
          <Skeleton className="h-14 w-full mb-3" />
          <Skeleton className="h-14 w-3/4 mb-8" />
          <Skeleton className="aspect-video w-full mb-8" />
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-4 w-full" />)}
          </div>
        </div>
      </NewsLayout>
    );
  }

  if (!article) {
    return (
      <NewsLayout theme={theme} siteName={siteName}>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="text-3xl font-bold mb-4">Article not found</h1>
          <Link to="/">
            <Button><ArrowLeft className="h-4 w-4 mr-2" />Back to home</Button>
          </Link>
        </div>
      </NewsLayout>
    );
  }

  const cleanContent = stripHtmlToText(article.content);
  const wordCount = cleanContent ? cleanContent.split(/\s+/).length : 0;
  const description =
    clipText(article.meta_description, 160) ||
    clipText(article.summary || article.excerpt, 160) ||
    clipText(cleanContent, 160);
  const image = article.featured_image || article.image_url;
  const datePublished = article.published_at || article.created_at;
  const altSource = article.image_alt || article.summary;
  const imageAlt = altSource
    ? clipText(altSource, 120)
    : `${article.title} — cover image`;
  const keywords = Array.isArray(article.meta_keywords) && article.meta_keywords.length
    ? article.meta_keywords
    : Array.isArray(article.tags) && article.tags.length
      ? article.tags
      : article.category
        ? [article.category, siteName]
        : undefined;

  const ld = [
    newsArticleLd({
      headline: article.title,
      description,
      image,
      imageWidth: 1200,
      imageHeight: 630,
      datePublished,
      dateModified: article.updated_at,
      authorName: article.author_name,
      siteName,
      siteUrl,
      logoUrl: publicProject?.logo || `${siteUrl}/favicon.ico`,
      articleUrl,
      category: article.category,
      keywords,
      articleBody: cleanContent,
      wordCount,
      language: "en",
    }),
    breadcrumbLd([
      { name: "Home", url: siteUrl + "/" },
      { name: article.category, url: `${siteUrl}/category/${article.category}` },
      { name: article.title, url: articleUrl },
    ]),
  ];

  const copyLink = () => {
    navigator.clipboard.writeText(articleUrl);
    toast.success("Link copied");
  };

  return (
    <NewsLayout theme={theme} siteName={siteName}>
      <Seo
        title={article.title}
        description={description}
        image={image}
        imageAlt={imageAlt}
        imageWidth={1200}
        imageHeight={630}
        canonical={articleUrl}
        siteName={siteName}
        type="article"
        publishedTime={datePublished}
        modifiedTime={article.updated_at}
        author={article.author_name}
        category={article.category}
        tags={article.tags}
        keywords={keywords}
        jsonLd={ld}
      />
      {theme === "espresso" && <EspressoArticle article={article} related={related} onCopy={copyLink} url={articleUrl} />}
      {theme === "rio" && <RioArticle article={article} related={related} onCopy={copyLink} url={articleUrl} />}
      {theme !== "espresso" && theme !== "rio" && <BusinessArticle article={article} related={related} />}
    </NewsLayout>
  );
};

/* ============================================================
 * ESPRESSO — elegant editorial reading column
 * ============================================================ */
const EspressoArticle = ({ article, related, onCopy, url }) => (
  <article className="pt-12 pb-24" data-testid="article-page">
    {/* Breadcrumb + category */}
    <div className="max-w-3xl mx-auto px-6 mb-8">
      <nav aria-label="Breadcrumb" className="font-meta mb-6" style={{ color: "var(--em-text-muted)" }}>
        <Link to="/" className="hover:underline">Home</Link> &nbsp;/&nbsp;
        <Link to={`/category/${article.category}`} className="hover:underline" style={{ color: "var(--em-accent)" }}>{article.category}</Link>
      </nav>
      <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight mb-6" style={{ color: "var(--em-text)" }}>
        {article.title}
      </h1>
      {article.excerpt && (
        <p className="text-xl leading-relaxed mb-8" style={{ color: "var(--em-text-muted)", fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}>
          {article.excerpt}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-meta" style={{ color: "var(--em-text-muted)" }}>
        <span>By {article.author_name || "Editorial"}</span>
        <span>·</span>
        <span>{formatDate(article.created_at)}</span>
        <span>·</span>
        <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3" /> {formatReadTime(article.content)} min read</span>
      </div>
    </div>

    {/* Hero image */}
    {article.featured_image && (
      <div className="max-w-5xl mx-auto px-6 mb-12">
        <div className="em-image-wrap aspect-[16/9] overflow-hidden">
          <img
            src={article.featured_image}
            alt={article.image_alt || `${article.title} — ${article.category} cover photo`}
            width="1200"
            height="675"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
        </div>
      </div>
    )}

    {/* Body */}
    <div className="max-w-2xl mx-auto px-6">
      <div className="em-prose text-lg leading-[1.85] space-y-6" style={{ color: "var(--em-text)" }}>
        {(article.content || "").split(/\n\n+/).map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </div>

      {/* Tags */}
      {article.tags?.length > 0 && (
        <div className="mt-12 pt-8" style={{ borderTop: "1px solid var(--em-border)" }}>
          <p className="font-meta mb-3" style={{ color: "var(--em-text-muted)" }}>Topics</p>
          <div className="flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span key={t} className="px-3 py-1.5 text-sm rounded-full" style={{ backgroundColor: "var(--em-surface)", color: "var(--em-text)" }}>
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Share */}
      <div className="mt-10 pt-8 flex items-center justify-between" style={{ borderTop: "1px solid var(--em-border)" }}>
        <p className="font-meta" style={{ color: "var(--em-text-muted)" }}>Share this story</p>
        <div className="flex items-center gap-3">
          <a target="_blank" rel="noopener noreferrer" href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(article.title)}`} className="p-2 rounded-full hover:bg-[var(--em-surface)]" aria-label="Share on Twitter"><Twitter className="h-4 w-4" /></a>
          <a target="_blank" rel="noopener noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} className="p-2 rounded-full hover:bg-[var(--em-surface)]" aria-label="Share on Facebook"><Facebook className="h-4 w-4" /></a>
          <a target="_blank" rel="noopener noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} className="p-2 rounded-full hover:bg-[var(--em-surface)]" aria-label="Share on LinkedIn"><Linkedin className="h-4 w-4" /></a>
          <button onClick={onCopy} className="p-2 rounded-full hover:bg-[var(--em-surface)]" aria-label="Copy link"><LinkIcon className="h-4 w-4" /></button>
        </div>
      </div>
    </div>

    {/* Related */}
    {related.length > 0 && (
      <section className="max-w-5xl mx-auto px-6 mt-24">
        <h2 className="font-display text-3xl font-bold mb-8" style={{ color: "var(--em-text)" }}>Keep reading</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {related.map((r) => (
            <Link key={r.id} to={`/article/${r.slug}`} className="block group">
              <div className="em-image-wrap aspect-[5/4] mb-4">
                {r.featured_image ? <img src={r.featured_image} alt={r.image_alt || `${r.title} — ${r.category} story`} width="600" height="480" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full" style={{ backgroundColor: "var(--em-surface)" }} />}
              </div>
              <p className="font-meta mb-2" style={{ color: "var(--em-accent)" }}>{r.category}</p>
              <h3 className="font-display text-lg font-bold leading-snug group-hover:opacity-80 transition-opacity" style={{ color: "var(--em-text)" }}>
                {r.title}
              </h3>
            </Link>
          ))}
        </div>
      </section>
    )}
  </article>
);

/* ============================================================
 * RIO — aggressive monospace reading
 * ============================================================ */
const RioArticle = ({ article, related, onCopy, url }) => (
  <article className="pb-16" data-testid="article-page">
    {/* meta dateline */}
    <div className="max-w-7xl mx-auto px-4 lg:px-6 pt-6 pb-2 font-mono text-xs uppercase flex items-center gap-3" style={{ color: "var(--rio-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
      <span className="rio-live-dot" />
      <span style={{ color: "var(--rio-accent)" }}>{article.category}</span>
      <span>·</span>
      <span>{new Date(article.created_at).toUTCString().replace("GMT", "UTC")}</span>
      <span>·</span>
      <span>BY {article.author_name?.toUpperCase() || "EDITORIAL"}</span>
      <span>·</span>
      <span>{formatReadTime(article.content)} MIN READ</span>
    </div>

    {/* Massive headline */}
    <div className="max-w-7xl mx-auto px-4 lg:px-6 mt-4 mb-10">
      <h1 className="font-display uppercase leading-[0.92]" style={{ color: "var(--rio-text)", fontFamily: "'Anton', sans-serif", fontSize: "clamp(2.5rem, 6vw, 5.5rem)", letterSpacing: "-0.01em" }}>
        {article.title}
      </h1>
      {article.excerpt && (
        <p className="mt-6 text-xl max-w-3xl" style={{ color: "var(--rio-text-muted)" }}>
          {article.excerpt}
        </p>
      )}
    </div>

    {/* Hero — full width hard edges */}
    {article.featured_image && (
      <div className="max-w-7xl mx-auto px-4 lg:px-6 mb-10">
        <div className="aspect-[21/9] overflow-hidden" style={{ border: "1px solid var(--rio-border)" }}>
          <img
            src={article.featured_image}
            alt={article.image_alt || `${article.title} — ${article.category} cover photo`}
            width="1400"
            height="600"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
        </div>
      </div>
    )}

    {/* Body — wider column */}
    <div className="max-w-3xl mx-auto px-6 text-lg leading-[1.75]" style={{ color: "var(--rio-text)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div className="space-y-5">
        {(article.content || "").split(/\n\n+/).map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </div>

      {article.tags?.length > 0 && (
        <div className="mt-12 pt-6 rio-divider" style={{ borderTop: "1px solid var(--rio-border)" }}>
          <p className="font-mono text-xs uppercase mb-3" style={{ color: "var(--rio-accent)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.2em" }}>Tags</p>
          <div className="flex flex-wrap gap-2 font-mono text-xs uppercase" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {article.tags.map((t) => (
              <span key={t} className="px-3 py-1.5" style={{ border: "1px solid var(--rio-border)", color: "var(--rio-text-muted)" }}>#{t}</span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 pt-6 flex items-center justify-between" style={{ borderTop: "1px solid var(--rio-border)" }}>
        <p className="font-mono text-xs uppercase" style={{ color: "var(--rio-text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.2em" }}>SHARE</p>
        <div className="flex items-center gap-2">
          <a target="_blank" rel="noopener noreferrer" href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(article.title)}`} className="p-2" style={{ border: "1px solid var(--rio-border)" }} aria-label="Share on Twitter"><Twitter className="h-4 w-4" /></a>
          <a target="_blank" rel="noopener noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} className="p-2" style={{ border: "1px solid var(--rio-border)" }} aria-label="Share on Facebook"><Facebook className="h-4 w-4" /></a>
          <a target="_blank" rel="noopener noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} className="p-2" style={{ border: "1px solid var(--rio-border)" }} aria-label="Share on LinkedIn"><Linkedin className="h-4 w-4" /></a>
          <button onClick={onCopy} className="p-2" style={{ border: "1px solid var(--rio-border)" }} aria-label="Copy link"><LinkIcon className="h-4 w-4" /></button>
        </div>
      </div>
    </div>

    {/* Related */}
    {related.length > 0 && (
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-20">
        <h2 className="font-display uppercase text-3xl md:text-4xl mb-6 pb-3" style={{ color: "var(--rio-text)", borderBottom: "2px solid var(--rio-accent)" }}>
          Next Wire
        </h2>
        <div className="grid grid-cols-12 gap-0" style={{ border: "1px solid var(--rio-border)" }}>
          {related.map((r) => (
            <Link key={r.id} to={`/article/${r.slug}`} className="col-span-12 sm:col-span-6 lg:col-span-3 block group" style={{ borderRight: "1px solid var(--rio-border)", borderBottom: "1px solid var(--rio-border)" }}>
              <div className="aspect-[4/3] overflow-hidden relative">
                {r.featured_image ? <img src={r.featured_image} alt={r.image_alt || `${r.title} — ${r.category} wire`} width="600" height="450" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" loading="lazy" /> : <div className="w-full h-full" style={{ backgroundColor: "var(--rio-surface)" }} />}
              </div>
              <div className="p-4">
                <p className="font-mono text-[0.65rem] uppercase mb-2" style={{ color: "var(--rio-accent)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.2em" }}>{r.category}</p>
                <h3 className="font-display text-base uppercase leading-tight" style={{ color: "var(--rio-text)" }}>{r.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>
    )}
  </article>
);

const BusinessArticle = ({ article, related }) => (
  <article className="max-w-3xl mx-auto px-4 py-12">
    <Link to="/" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-6"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Link>
    <h1 className="text-4xl font-bold mb-4">{article.title}</h1>
    <p className="text-sm text-slate-500 mb-8">By {article.author_name} · {formatDate(article.created_at)}</p>
    {article.featured_image && <img src={article.featured_image} alt={article.image_alt || `${article.title} — ${article.category} cover photo`} width="1200" height="675" className="w-full aspect-video object-cover rounded-xl mb-8" fetchPriority="high" />}
    <div className="prose max-w-none space-y-4">
      {(article.content || "").split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}
    </div>
    {related.length > 0 && (
      <div className="mt-16">
        <h2 className="text-2xl font-bold mb-6">Related</h2>
        <div className="grid grid-cols-2 gap-6">
          {related.map((r) => (
            <Link key={r.id} to={`/article/${r.slug}`} className="block">
              <h3 className="font-semibold">{r.title}</h3>
            </Link>
          ))}
        </div>
      </div>
    )}
  </article>
);

export default NewsArticle;
